import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { formatKickoff, formatScore } from "@/pages/matches/lib/match-format";
import { IMatchData } from "@/types/match";

const options = (...values: string[]) =>
  values.map((value) => ({ label: value, value }));

const enumColumn = (
  id: string,
  header: string,
  key: string,
  opts: { label: string; value: string }[] | undefined,
  filterType: "faceted-select" | "select" = "faceted-select",
): ColumnDef<IMatchData> => ({
  id,
  header,
  accessorKey: `document.${key}`,
  enableSorting: false,
  size: 120,
  meta: { filterType, ...(opts ? { options: opts } : {}) },
});

const booleanOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const useMatchColumns = () =>
  React.useMemo<ColumnDef<IMatchData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "startDate",
        header: "Kickoff",
        size: 150,
        accessorFn: (row) => row.document.startDate,
        enableSorting: true,
        cell: function render({ getValue }: any) {
          return makeTooltip(formatKickoff(getValue()));
        },
        meta: { filterType: "date" },
      },
      {
        id: "league.name",
        header: "League",
        size: 180,
        accessorFn: (row) => row.document.league?.name ?? "—",
        enableSorting: false,
        cell: function render({ row, getValue }: any) {
          const leagueId = row.original.document.leagueId;
          return makeTooltip(
            leagueId ? (
              <Link
                to={`/leagues/show/${leagueId}`}
                className="block truncate hover:underline"
              >
                {getValue()}
              </Link>
            ) : (
              <div className="truncate">{getValue()}</div>
            ),
          );
        },
        meta: { filterType: "text" },
      },
      {
        id: "match",
        header: "Match",
        minSize: 240,
        size: 320,
        accessorFn: (row) =>
          `${row.document.homeTeam?.name ?? "—"} v ${row.document.awayTeam?.name ?? "—"}`,
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/matches/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
            >
              {getValue()}
            </Link>,
          );
        },
      },
      {
        id: "score",
        header: "Score",
        size: 80,
        accessorFn: (row) =>
          formatScore(row.document.homeScore, row.document.awayScore),
        enableSorting: false,
        enableColumnFilter: false,
      },
      enumColumn(
        "state",
        "State",
        "state",
        options("Scheduled", "Postponed", "Cancelled", "Live", "Finished"),
      ),
      {
        ...enumColumn(
          "status",
          "Status",
          "status",
          options("Draft", "Complete", "Hidden"),
          "select",
        ),
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
      },
      enumColumn(
        "venueSource",
        "Venue source",
        "venueSource",
        options("Api", "HomeTeamDefault", "Curator"),
      ),
      {
        ...enumColumn("hide", "Hidden", "hide", booleanOptions, "select"),
        cell: ({ getValue }: any) =>
          getValue() ? <Badge variant="secondary">Hidden</Badge> : null,
      },
      enumColumn(
        "sport",
        "Sport",
        "sport",
        options("Football", "Basketball", "Volleyball"),
      ),
      {
        id: "league.country",
        header: "Country",
        accessorFn: (row) => row.document.league?.country,
        enableSorting: false,
        size: 140,
        meta: { filterType: "faceted-select" },
      },
      {
        id: "venue.name",
        header: "Venue",
        size: 200,
        accessorFn: (row) => row.document.venue?.name ?? "—",
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
